
/**
 * Module dependencies.
 */

import { MouseEvent, ReactNode } from 'react';
import { RouterLink } from '../links/router-link';
import { buttonFontSizes } from './styles';
import { color, media } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import { typography } from '../typography/typography-styles';
import styled, { css } from 'styled-components';

/**
 * `ColorTheme` type.
 */

type ColorTheme = 'primary' | 'secondary';

/**
 * `Size` type.
 */

type Size = 'large' | 'medium' | 'small';

/**
 * `FlatButtonProps` type.
 */

type FlatButtonProps = {
  children: ReactNode;
  colorTheme?: ColorTheme;
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  responsive?: boolean;
  size?: Size;
  to?: any;
  type?: 'button' | 'submit';
};

/**
 * `Button` styled component.
 */

const Button = styled.button<{ colorTheme: ColorTheme; fullWidth: boolean; responsive: boolean; size: Size }>`
  appearance: none;
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: inline-block;
  font-family: ${typography.fontFamilies.saira};
  padding: 0;
  outline: none;
  text-decoration: none;
  transition: ${theme('animations.defaultTransition')};
  transition-property: border-color, color;

  ${switchProp('colorTheme', {
    primary: css`
      color: ${color('primary')};

      &:active,
      &:visited {
        color: ${color('primary')};
      }

      &:focus-visible {
        border: 2px solid ${color('blue400')};
        color: ${color('blue600')};
      }

      &:focus,
      &:hover {
        color: ${color('blue600')};
      }

      &[aria-disabled=true] {
        color: ${color('blue200')};
      }
    `,
    secondary: css`
      color: ${color('gray500')};

      &:active,
      &:visited {
        color: ${color('gray500')};
      }

      &:focus-visible {
        border: 2px solid ${color('blue400')};
        color: ${color('gray600')};
      }

      &:focus,
      &:hover {
        color: ${color('gray600')};
      }

      &[aria-disabled=true] {
        color: ${color('gray400')};
      }
    `
  })}

  ${switchProp('size', {
    large: css`
      ${buttonFontSizes.large}

      ${media.max('md')`
        ${ifProp('responsive', buttonFontSizes.small)}
      `}
    `,
    medium: css`
      font-size: 16px;
      font-weight: 500;
      letter-spacing: -0.2px;
      line-height: 21px;
    `,
    small: css`
      font-size: 13px;
      font-weight: 500;
      letter-spacing: normal;
      line-height: 20px;
    `
  })};

  ${ifProp('fullWidth', css`
    text-align: center;
    width: 100%;
  `)}
  
  &:disabled,
  &[aria-disabled=true] {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * Export `FlatButton` component.
 */

export function FlatButton(props: FlatButtonProps): JSX.Element {
  const {
    colorTheme = 'primary',
    disabled,
    fullWidth = false,
    href,
    onClick,
    responsive = true,
    size = 'small',
    to,
    type = 'button',
    ...rest
  } = props;

  return (
    <Button
      {...href ? { as: 'a', href } : {}}
      {...to ? { as: RouterLink, onClick, to } : {}}
      {...onClick && !to ? { disabled, onClick, type } : {}}
      {...rest}
      aria-disabled={disabled}
      colorTheme={colorTheme}
      fullWidth={fullWidth}
      responsive={responsive}
      size={size}
    />
  );
}

